import type { MouseEventHandler } from 'react';
import styles from './DatePickerListItem.module.css';

/**
 * Figma: `_Date picker list item` (123:2178) — "Internal sub-component for the
 * pre-set date range sidebar. Renders a clickable text row for quick range
 * selection (e.g. "Today", "Last week", "This month"). Variants: State
 * (Default, Hover, Active). Font: Inter Regular 14px. Padding: 8px uniform.
 * Corner radius: 6px. Layout: Horizontal auto-layout. Width: 160px."
 *
 * `State=Active` is which preset is currently applied — real state, so it is the
 * `selected` prop rather than a variant enum. Hover is CSS. The 160px width is
 * the component set's own frame; every instance in `_Date picker menu` fills its
 * 152px-wide column instead, so the row fills its parent here too.
 */
export type DatePickerListItemState = 'default' | 'hover' | 'active';

export type DatePickerListItemProps = {
  label: string;
  /** Figma `State=Active`. */
  selected?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /**
   * Normally leave this alone: hover is CSS and `active` is the `selected`
   * prop. Forcing a state is for documentation and visual regression, where all
   * three variants need to render side by side.
   */
  state?: DatePickerListItemState;
  className?: string;
};

export function DatePickerListItem({
  label,
  selected = false,
  onClick,
  state,
  className,
}: DatePickerListItemProps) {
  const active = selected || state === 'active';

  const classes = [
    styles.item,
    active ? styles.active : null,
    state === 'hover' ? styles.hover : null,
    state === undefined ? styles.interactive : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    /*
     * `aria-pressed` rather than `aria-current`: these are not navigation, they
     * are a set of mutually exclusive shortcuts, and the pressed one is the range
     * currently applied. Not in Figma.
     */
    <button className={classes} type="button" onClick={onClick} aria-pressed={active}>
      <span className={styles.label}>{label}</span>
    </button>
  );
}
